import React, { MouseEvent, useCallback, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  Menu,
  MenuItem,
  styled,
  Tooltip
} from '@mui/material';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  TranslatedText,
  useLangCookieSetter,
  useLocale,
  useLocaleRedirect,
  useTranslator
} from '@ianesthesie-kit/core';
import { logout, useCurrentUser } from '@ianesthesie-kit/redux';
import { useAppDispatch } from '@/hooks';
import { useUpdateUserLanguageMutation } from '@/modules';
import { flagsMap, GhostUserIcon } from '@/components/icon';
import {
  AvailableLocale,
  isAvailableLocale,
  languageMap,
  themeColors
} from '@ianesthesie-kit/config';
import { SentryService } from '@ianesthesie-kit/sentry';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import { availableLocales } from '@/config';

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (p) => p !== 'selected'
})<{ selected: boolean }>`
  border-color: ${(p) =>
    p.selected ? p.theme.palette.primary.main : 'transparent'};
  border-width: 3px;
  border-style: solid;
  aspect-ratio: 1 / 1;
  width: 28px;
  height: 28px;
  overflow: hidden;
  box-sizing: border-box;

  svg {
    min-width: 36px;
    min-height: 36px;
  }
`;

const FlagButton = ({
  locale,
  selected,
  ...btnProps
}: IconButtonProps & { locale: AvailableLocale; selected: boolean }) => {
  const Flag = flagsMap[locale];
  return (
    <StyledIconButton selected={selected} size="small" {...btnProps} sx={{}}>
      <Flag width={32} height={32} />
    </StyledIconButton>
  );
};

const LangButtons = ({ onClose }: { onClose: () => void }) => {
  const currentLocale = useLocale();

  const [updateUserLang] = useUpdateUserLanguageMutation();
  const redirectToLocale = useLocaleRedirect();
  const setLocaleCookie = useLangCookieSetter();

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const newLocale = e.currentTarget.dataset.locale;
      if (currentLocale === newLocale || !newLocale) {
        return;
      }
      if (!isAvailableLocale(newLocale)) {
        const err = new Error(`Unsupported locale: ${newLocale}`);
        SentryService.getInstance().captureException(err);
        throw err;
      }

      redirectToLocale(newLocale);
      setLocaleCookie(newLocale);
      updateUserLang(languageMap[newLocale])
        .catch((err) => {
          SentryService.getInstance().captureException(err);
        })
        .finally(onClose);
    },
    [currentLocale, redirectToLocale, onClose, setLocaleCookie, updateUserLang]
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1
      }}
    >
      {availableLocales?.map((loc) => (
        <FlagButton
          key={loc}
          onClick={onClick}
          data-locale={loc}
          locale={loc as AvailableLocale}
          selected={loc === currentLocale}
        />
      ))}
    </Box>
  );
};

export function UserMenu() {
  const [buttonAnchor, setButtonAnchor] = useState<null | HTMLElement>(null);
  const user = useCurrentUser();
  const dispatch = useAppDispatch();
  const translate = useTranslator();

  if (!user) {
    return null;
  }

  const creds =
    user.medicalCredentials &&
    translate(
      `doctor-dashboard.medicalCreds.${user.medicalCredentials}`,
      user.medicalCredentials
    );
  const suff = creds ? ` (${creds})` : '';
  const userDescription = `${user.firstName} ${user.lastName}${suff} `;

  const userInitials = `${user.firstName?.slice(0, 1)}${user.lastName?.slice(
    0,
    1
  )}`;

  const isUserExcludedFromTracking = user.excludeFromTracking;

  return (
    <>
      <Button
        variant="text"
        endIcon={<ArrowDownIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          color: '#9ca0ae'
        }}
        onClick={(e) => setButtonAnchor(e.currentTarget)}
      >
        <Badge
          invisible={!isUserExcludedFromTracking}
          variant="standard"
          badgeContent={
            <Tooltip title={'You are excluded from tracking'}>
              <GhostUserIcon
                fill={themeColors.green[500]}
                width={16}
                height={16}
              />
            </Tooltip>
          }
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          <span>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: 12,
                display: 'inline-flex',
                marginRight: 1
              }}
            >
              {userInitials}
            </Avatar>
          </span>
        </Badge>
        {userDescription}
      </Button>
      <Menu
        anchorEl={buttonAnchor}
        open={!!buttonAnchor}
        onClose={() => setButtonAnchor(null)}
      >
        <MenuItem>
          <LangButtons onClose={() => setButtonAnchor(null)} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => dispatch(logout())}>
          <ListItemIcon>
            <LogoutRounded />
          </ListItemIcon>
          <TranslatedText id="app.logout" />
        </MenuItem>
      </Menu>
    </>
  );
}
